<template>
  <b-row>
    <b-col>
      <b-btn
        v-if="hasReadyForImport"
        size="lg"
        variant="success"
        class="mt-4 mb-4"
        @click="startUpload"
      >
        Begin upload <v-icon name="cloud-upload-alt" />
      </b-btn>

      <div v-if="currentFileUploadingName && upload_inprogress" class="mt-4">
        <h4>
          {{ queue.length + 1 }}
          {{ "file" | pluralize(queue.length + 1) }} remaining to upload ...
        </h4>

        <b-progress
          variant="success"
          :value="currentFileUploadingPercentage"
          :max="100"
          class="mb-3 mt-3"
        />
        <p>
          Uploading <strong>{{ currentFileUploadingName }}</strong> ...
        </p>
      </div>

      <div v-if="displayErrorMessage" class="mt-2 alert alert-danger">
        <p>
          An error occurred uploading
          <strong>{{ currentFileUploadingName }}</strong> to the server.
        </p>
        <p v-if="displayErrorAllowRetry">
          <b-btn @click="retryCurrentQueueItem">Retry Previous File</b-btn>
        </p>
        <p v-if="displayErrorClientNetworkError">
          Network Error. Please check your internet connection try again.
        </p>
        <p v-else>Please check your media and try again.</p>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import axios from "axios";
import reject from "lodash/reject";
import * as Sentry from "@sentry/browser";

export default {
  name: "AssetImporter",
  filters: {
    pluralize(word, amount) {
      if (amount > 1) {
        return `${word}s`;
      }
      return word;
    }
  },
  components: {},
  props: {
    show: {
      type: Object,
      required: true,
      default: () => {}
    },
    image_files: {
      type: Array,
      default: () => []
    },
    audio_file: {
      type: File,
      default: () => {}
    },
    video_files: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      key: "",
      fields: {},
      file: null,
      zip_file: null,
      size: 0,
      zip_name: "",
      currentFileUploadingPercentage: 0,
      currentFileUploadingName: null,
      currentQueueItem: null,
      file_list: [],
      show_details: {},
      upload_inprogress: false,
      queue: [],
      fileTotalUploadedPercentage: 0,
      file_total_number_files: null,
      audio_preview: null,
      displayErrorMessage: false,
      displayErrorAllowRetry: false,
      displayErrorClientNetworkError: false,
      minimumTimeBetweenUploadImagesMs: 1500,
      lastTimeImageStartMs: null
    };
  },
  computed: {
    ready() {
      return this.key !== "";
    },
    authorized() {
      return this.$store.state.authorized;
    },
    user_plan() {
      return this.$store.getters.user_plan;
    },
    shows_in_progress() {
      return reject(this.$store.state.legacy_shows, { video_completed: true })
        .length;
    },
    id() {
      return this.show.key;
    },
    hasReadyForImport() {
      return (
        this.image_files.length > 0 ||
        this.video_files.length > 0 ||
        this.audio_file
      );
    }
  },
  watch: {},
  mounted() {
    // check upload credentials.
    this.$store.dispatch("editor/updateCredentialsIfNeeded", this.show);
    this.queue = [];
  },
  methods: {
    callback(show_details) {
      console.log(show_details);
      this.readDataFileAndPopulateUploadQueue();
    },
    uploadNextInQueue() {
      this.upload_inprogress = true;
      this.displayErrorMessage = false;
      // Set file_total_number_files on first call.
      if (!this.file_total_number_files) {
        this.file_total_number_files = this.queue.length;
      }
      this.fileTotalUploadedPercentage =
        100 -
        Math.ceil(
          (this.queue.length / (this.file_total_number_files + 1)) * 100
        );
      if (this.queue.length < 1) {
        // All done.
        this.importComplete();
        console.log("all done");
        this.upload_inprogress = false;
        return false;
      }
      var item = this.queue.shift();
      this.currentQueueItem = item;
      if (item.type === "image") {
        this.uploadImage({
          name: item.name,
          fileObj: item.fileObj,
          type: "image"
        });
      }
      if (item.type === "video") {
        this.uploadVideo({
          name: item.name,
          fileObj: item.fileObj,
          type: "video"
        });
      }
      if (item.type === "audio") {
        // Audio is thus uploaded last.
        this.uploadAudio(item);
      }
    },
    importComplete() {
      console.log("importComplete");
      this.$store.dispatch("show/importCurrentShowComplete");
    },
    retryCurrentQueueItem() {
      // Push back to front of queue.
      this.queue.unshift(this.currentQueueItem);
      // Reset all the error messages.
      this.displayErrorMessage = false;
      this.displayErrorAllowRetry = false;
      this.displayErrorClientNetworkError = false;
      // Wait 1 second to restart. Gives illusion that UI is reconnecting.
      // This is important if the user HAS NOT been able to reconnect, as
      // we want them to see that the UI has actually retried.
      setTimeout(this.uploadNextInQueue, 1000);
    },
    uploadAudio(audio) {
      var _v = this;
      let formData = new FormData();
      console.log("audio", audio);
      // Is it an mp3 file.
      var requiresConversion = true;
      if (audio.name.endsWith(".mp3") && audio.fileObj.type != "audio/x-m4a") {
        requiresConversion = false;
      }
      var filename = Date.now() + "_audio.mp3";
      var deployed_filename = filename;
      var fields = this.show.audioCredentialsV4.fields;
      var bucket = this.show.audioCredentialsV4.bucket;
      if (requiresConversion) {
        filename = Date.now() + "_" + audio.name.replace(/[^a-z0-9.]+/gi, "_");
        deployed_filename = filename + ".mp3";
        fields = this.show.audioWildcardCredentialsV4.fields;
        bucket = this.show.audioWildcardCredentialsV4.bucket;
      }
      var source_id = this.id;
      var key = "audio/" + this.id + "/" + filename;

      for (const [key, value] of Object.entries(fields)) {
        console.log(key, value);
        // Don't add key, we want the slug as the name.
        if (key != "key") {
          formData.append(key, value);
        }
      }
      formData.append("key", key); // Necessary as fields key includes ${filename}
      formData.append("acl", "public-read");
      formData.append("success_action_status", "201");

      if (requiresConversion) {
        var file_type = audio.fileObj.type;

        if (file_type === "video/ogg") {
          // Rename .ogg file types because of this Firefox bug.
          // In FF, all .ogg files are typed as "video/ogg".
          // https://bugzilla.mozilla.org/show_bug.cgi?id=1240259
          file_type = "audio/ogg";
        }
        if (file_type === "" && audio.name.endsWith(".ogg")) {
          // Safari also has issues with .ogg files.
          // Safari may omit the file type completely.
          file_type = "audio/ogg";
        }

        formData.append("Content-Type", file_type);
      } else {
        // Plain ole mp3, no conversion needed.
        formData.append("Content-Type", "audio/mpeg");
        // Use below content-type if you want to simulate an S3 403 error.
        // formData.append("Content-Type", "video/srv");
      }

      formData.append("Cache-Control", "public, max-age=315576000, immutable");
      formData.append("file", audio.fileObj);
      console.log(">> formData >> ", formData);
      _v.currentFileUploadingPercentage = 0;
      _v.currentFileUploadingName = audio.name;

      // You should have a server side REST API
      axios
        .post(`https://${bucket}.s3.amazonaws.com/`, formData, {
          onUploadProgress: progressEvent => {
            console.log(progressEvent);
            var _uploaded = Math.ceil(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            _v.currentFileUploadingPercentage = _uploaded;
          }
        })
        .then(function(e) {
          console.log(e);
          console.log("SUCCESS!!");
          _v.audioUploaded({
            source_id: source_id,
            filename: deployed_filename,
            name: audio.name
          });
        })
        .catch(error => {
          // This can catch all kinds of errors, not just axios uploads.
          console.log(error);
          _v.logError(error);
        });
    },
    uploadImage(image) {
      var _v = this;
      _v.lastTimeImageStartMs = new Date().getTime();
      let formData = new FormData();
      // Make a clean filename.
      var filename =
        Date.now() + "_" + image.name.replace(/[^a-z0-9.]+/gi, "_");
      var filename_without_extension = filename
        .split(".")
        .slice(0, -1)
        .join(".");
      // Ensure filename limit is < 100 characters.
      // S3 key name limit is 1024, but there's no need to be excessive.
      filename = filename_without_extension.substring(0, 96) + ".jpg";
      //
      var source_id = this.id;
      var key = this.id + "/" + filename;
      console.log("key", key);
      var fields = this.show.imageCredentialsV4.fields;
      for (const [key, value] of Object.entries(fields)) {
        console.log(key, value);
        // Don't add key, we want the slug as the name.
        if (key != "key") {
          formData.append(key, value);
        }
      }
      formData.append("key", key); // Necessary as fields key includes ${filename}
      formData.append("acl", "public-read");
      formData.append("success_action_status", "201");
      formData.append("Content-Type", "image/jpeg");
      formData.append("Cache-Control", "public, max-age=315576000, immutable");
      formData.append("file", image.fileObj);
      console.log(">> formData >> ", formData);
      _v.currentFileUploadingPercentage = 0;
      _v.currentFileUploadingName = image.name;
      // You should have a server side REST API
      axios
        .post(
          `https://${this.show.imageCredentialsV4.bucket}.s3.amazonaws.com/`,
          formData,
          {
            onUploadProgress: progressEvent => {
              console.log(progressEvent);
              var _uploaded = Math.ceil(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              _v.currentFileUploadingPercentage = _uploaded;
            }
          }
        )
        .then(function(e) {
          console.log(e);
          console.log("SUCCESS!!");
          _v.imageUploaded({
            source_id: source_id,
            filename: filename,
            name: image.name
          });
        })
        .catch(error => {
          // This can catch all kinds of errors, not just axios uploads.
          console.log(error);
          _v.logError(error);
        });
    },
    uploadVideo(video) {
      var _v = this;
      let formData = new FormData();
      console.log("video", video);

      var fields = this.show.videoWildcardCredentialsV4.fields;
      var bucket = this.show.videoWildcardCredentialsV4.bucket;

      var filename =
        Date.now() + "_" + video.name.replace(/[^a-z0-9.]+/gi, "_");
      var deployed_filename = filename + ".mp4";

      var source_id = this.id;
      var key = "video/" + this.id + "/" + filename;

      for (const [key, value] of Object.entries(fields)) {
        console.log(key, value);
        // Don't add key, we want the slug as the name.
        if (key != "key") {
          formData.append(key, value);
        }
      }
      formData.append("key", key); // Necessary as fields key includes ${filename}
      formData.append("acl", "public-read");
      formData.append("success_action_status", "201");

      var file_type = video.fileObj.type;
      formData.append("Content-Type", file_type);
      formData.append("Cache-Control", "public, max-age=315576000, immutable");
      formData.append("file", video.fileObj);
      console.log(">> formData >> ", formData);
      _v.currentFileUploadingPercentage = 0;
      _v.currentFileUploadingName = video.name;

      // You should have a server side REST API
      axios
        .post(`https://${bucket}.s3.amazonaws.com/`, formData, {
          onUploadProgress: progressEvent => {
            console.log(progressEvent);
            var _uploaded = Math.ceil(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            _v.currentFileUploadingPercentage = _uploaded;
          }
        })
        .then(function(e) {
          console.log(e);
          console.log("SUCCESS!!");
          _v.videoUploaded({
            source_id: source_id,
            filename: deployed_filename,
            name: video.name
          });
        })
        .catch(error => {
          // This can catch all kinds of errors, not just axios uploads.
          console.log(error);
          _v.logError(error);
        });
    },
    startUpload() {
      console.log("startUpload");
      console.log(this.image_files);
      var _v = this;
      // Images
      this.image_files.forEach(i => {
        this.queue.push({
          name: i.name,
          fileObj: i,
          type: "image"
        });
      });
      // Videos
      this.video_files.forEach(i => {
        this.queue.push({
          name: i.name,
          fileObj: i,
          type: "video"
        });
      });
      if (!this.audio_file) {
        _v.$emit("uploadStarted");
        _v.uploadNextInQueue();
        return;
      }
      // Audio
      this.audio_preview = {
        name: this.audio_file.name,
        fileObj: this.audio_file,
        type: "audio"
      };
      _v.queue.push(_v.audio_preview);
      // Kick off the uploading.
      _v.$emit("uploadStarted");
      _v.uploadNextInQueue();
    },
    logError(error) {
      console.log("logError", error);
      // We should handle all errors by presenting.
      // Common errors: Wrong mimetype & timeout
      // Those should be handled with more care to the users.

      if ("response" in error) {
        console.log("error.response", error.response);
        console.log("error.message", error.message);
        console.log("error.status", error.status);
        var sentryLogMessage =
          "Generic error message from Axios in AssetImporter";

        // No response is a Network Error from Axios.
        // Most likely this is a Network drop from the client side.
        // Safe to retry.

        // Status 400, client or network error, retry is possible.
        // Most likely this is a Network Timeout.
        // S3 timeouts if no data is seen for 20 seconds.

        // Status 403, Forbidden, do not retry.
        // This is likely a S3 filetype mismatch.
        // User should select their audio/images again.
        // This is possible on iOS due to Safari being hella lame and
        // ignoring the 'accept' parameter for file formats in input.

        if (error.response) {
          if (error.response.status === 400) {
            this.displayErrorAllowRetry = true;
          }
          if (error.response.status === 403) {
            this.displayErrorAllowRetry = false;
          }
          sentryLogMessage = error.response.data;
        } else {
          // Error without a response is a Network Error.
          sentryLogMessage = "Network Error";
          this.displayErrorAllowRetry = true;
          this.displayErrorClientNetworkError = true;
        }

        console.log("Sentry message", sentryLogMessage);
        Sentry.configureScope(function(scope) {
          scope.setExtra("error.response.data", sentryLogMessage);
        });
      }
      Sentry.captureException(error);
      this.upload_inprogress = false;
      this.displayErrorMessage = true;
    },
    audioUploaded(audio) {
      console.log("audioUploaded");
      var new_audio = {
        source_id: audio.source_id,
        filename: audio.filename,
        name: audio.name,
        deployed: false
      };
      this.$store.dispatch("show/addNewAudio", new_audio);
      this.uploadNextInQueue();
    },
    imageUploaded(image) {
      console.log("imageUploaded", image);
      var new_image = {
        source_id: image.source_id,
        filename: image.filename,
        name: image.name
      };
      var _v = this;
      this.$store.dispatch("show/addNewImage", new_image).then(() => {
        //_v.uploadNextInQueue();
        var timeElapsedMs = new Date().getTime() - _v.lastTimeImageStartMs;
        console.log("timeElapsedMs", timeElapsedMs);
        if (timeElapsedMs < _v.minimumTimeBetweenUploadImagesMs) {
          var delayMs = _v.minimumTimeBetweenUploadImagesMs - timeElapsedMs;
          console.log("delaying", delayMs);
          setTimeout(_v.uploadNextInQueue, delayMs);
        } else {
          _v.uploadNextInQueue();
        }
      });
    },
    videoUploaded(video) {
      console.log("videoUploaded", video);
      var new_video = {
        source_id: video.source_id,
        filename: video.filename,
        name: video.name,
        type: "video",
        keep_sync: true
      };
      this.$store.dispatch("show/addNewVideo", new_video);
      this.uploadNextInQueue();
    }
  }
};
</script>

<style lang="scss" scoped></style>
